import React, { Fragment, useEffect, useState } from "react";

import { connect } from "react-redux";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { goBack, replace, routerActions } from "connected-react-router";
import * as i18n from "util/i18n";

import { formatNum } from "util/format";
import { actions as depositsActions, selectors as depositsSelectors } from "reducers/deposits";
import { ResponsiveHeader } from "pages/_components/header";

import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import { Col, Row } from "react-bootstrap";
import Credential from "pages/_components/fields/credentials/Credential";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import Button from "pages/_components/Button";
import { TransactionInfo } from "pages/_components/TransactionCard";
import FormattedDate from "pages/_components/FormattedDate";
import GridTable from "pages/_components/GridTable/GridTable";
import gaEventTracker, { events } from "util/gaEventTracker";
import { isDesktop, isIOS } from "react-device-detect";
import { useLocation } from "react-router-dom";
import CustomData from "pages/_components/CustomData/CustomData";
const CATEGORY = "fixedTermDeposit";

const NewDepositSummaryForm = ({
    dispatch,
    fetching,
    newDepositData,
    depositToken,
    depositRequiredCredentials,
    depositTicketStatus,
    depositTicketId,
}) => {
    const isLoading = fetching || !newDepositData?.depositType?.length || !depositRequiredCredentials?.length;
    const credentialType = depositRequiredCredentials.includes("otp") ? "otp" : "password";
    const [isValidating, setIsValidating] = useState(true);
    const { state: locationState = {} } = useLocation();
    const [formRef, setFormRef] = useState();
    const depositData = {
        depositType: newDepositData?.depositType,
        investCapital: newDepositData?.amountInvested || 0,
        debitAccount: {
            productAlias: newDepositData?.accreditedAccount?.productAlias,
            number: newDepositData?.accreditedAccount?.number,
            productType: newDepositData?.accreditedAccount?.productType,
        },
        termInDays: newDepositData?.coreDeadline,
        expirationAction: newDepositData?.autoRenewal,

        currency: newDepositData?.currency,
        earnedInterests: newDepositData?.interest,
        chargeAmount: newDepositData?.amountTransfered,
        tna: newDepositData?.rate,
        tea: newDepositData?.effectiveRate,
        expirationDate: newDepositData?.dueDate,

        retentions: newDepositData?.retentions,
        vats: newDepositData?.vats,
        profit: newDepositData?.profit,
        stamp: newDepositData?.stamp,
        jurisdiction: newDepositData?.jurisdiction,
    };
    const currencyString = i18n.get(`currency.label.${depositData?.currency}`);
    const accountTypeString = depositData?.debitAccount?.productType?.slice(0, 2);

    useEffect(() => {
        dispatch(depositsActions.fetchCredentialGroup());
    }, [dispatch]);

    useEffect(
        () => () => {
            dispatch(depositsActions.cleanNewDepositData());
        },
        [dispatch],
    );

    useEffect(() => {
        if (isValidating && depositTicketStatus === "INVALID_CREDENTIAL") {
            setIsValidating(false);
            formRef.setErrors({
                credential: i18n.get(
                    credentialType === "password"
                        ? "preferences.password.password.incorrect"
                        : "login.step1.secondFactor.invalid",
                ),
            });
        } else if (isValidating && depositTicketStatus === "LIMIT_ERROR") {
            setIsValidating(false);
        } else if (depositTicketStatus === "PENDING" && depositTicketId?.length) {
            dispatch(
                routerActions.push(`/deposits/pendingTransaction/${depositTicketId}`, {
                    isCreationFlow: !!locationState.isCreationFlow,
                }),
            );
        } else if (depositTicketStatus === "FINISHED" && depositTicketId?.length) {
            dispatch(depositsActions.acceptTermDeposit(depositTicketId, CATEGORY));

            dispatch(
                routerActions.push(`/deposits/successTransaction/${depositTicketId}`, {
                    isCreationFlow: !!locationState.isCreationFlow,
                }),
            );
        }
    }, [isValidating, formRef, depositTicketStatus, depositTicketId]);

    const handleSubmit = ({ credential }) => {
        setIsValidating(true);
        // gaEventTracker.triggerEvent(
        //     events.newFixed({
        //         productType: newDepositData?.depositType,
        //         term: newDepositData?.deadline,
        //         action: newDepositData?.autoRenewal,
        //     }),
        // );

        if (newDepositData?.idTransaction) {
            gaEventTracker.triggerEvent(events.pf_nuevo_constituir_firmar());

            dispatch(
                depositsActions.setDepositCredential(
                    {
                        idTransaction: newDepositData?.idTransaction,
                    },
                    { [`_${credentialType}`]: credential, token: depositToken },
                ),
            );
        } else {
            gaEventTracker.triggerEvent(
                events.pf_nuevo_constituir_enviar({
                    monto: newDepositData?.amountInvested,
                    cbu: newDepositData?.debitAccount?.number,
                }),
            );

            dispatch(
                depositsActions.setDepositCredential(newDepositData, {
                    [`_${credentialType}`]: credential,
                    token: depositToken,
                }),
            );
        }
    };

    const handleOnClickBack = () => {
        dispatch(depositsActions.newDepositFormShouldShowLastValues(true));
        // eslint-disable-next-line no-unused-expressions
        newDepositData?.idTransaction ? dispatch(replace("/pendingTransaction/list")) : dispatch(goBack());
    };

    const renderHeader = () => (
        <ResponsiveHeader
            id="deposits.summary.title"
            onClickBack={handleOnClickBack}
            className={isIOS && "whitespace-nowrap"}
        />
    );
    return (
        <Fragment>
            <Notification scopeToShow="deposits" />
            {!isLoading && renderHeader()}
            <MainContainer showLoader={isLoading}>
                <div className="container">
                    <h2 className="text-lead">{i18n.get("deposits.create.summaryTitle")}</h2>
                    <div className="transaction-ticket">
                        <GridTable className="form-summary deposit-summary details">
                            <GridTable.Container>
                                <GridTable.Data columnStart="1">
                                    <TransactionInfo
                                        field="deposits.type"
                                        i18nId={`deposit.depositTypes.${depositData?.depositType}`}
                                    />
                                </GridTable.Data>
                                <GridTable.Data columnStart="2">
                                    <TransactionInfo
                                        field="deposits.debitAccount"
                                        value={`····${depositData?.debitAccount?.productAlias?.slice(
                                            -6,
                                        )} ${accountTypeString} ${currencyString}`}
                                    />
                                </GridTable.Data>
                                <GridTable.Data columnStart="3">
                                    <TransactionInfo
                                        field="deposits.investCapital"
                                        value={`${currencyString} ${formatNum(depositData?.investCapital, 2)}`}
                                    />
                                </GridTable.Data>
                                <GridTable.Data columnStart="1" rowStart="2">
                                    <TransactionInfo
                                        field="deposits.earnedInterests"
                                        value={`${currencyString} ${formatNum(depositData?.earnedInterests, 2)}`}
                                    />
                                </GridTable.Data>
                                <GridTable.Data columnStart="2" rowStart="2">
                                    <TransactionInfo
                                        field="deposits.chargeAmount"
                                        value={`${currencyString} ${formatNum(depositData?.chargeAmount, 2)}`}
                                    />
                                </GridTable.Data>
                                <GridTable.Data columnStart="3" rowStart="2">
                                    <TransactionInfo field="deposits.expirationDate">
                                        <FormattedDate date={depositData?.expirationDate} dateFormat="DD/MM/YYYY" />
                                    </TransactionInfo>
                                </GridTable.Data>
                                <GridTable.Data columnStart="1" rowStart="3">
                                    {" "}
                                    <TransactionInfo field="deposits.termInDays" value={depositData?.termInDays} />
                                </GridTable.Data>
                                {/* <GridTable.Data columnStart="2" rowStart="3">
                                    <TransactionInfo
                                        field="deposits.teaDetail"
                                        value={`${formatNum(depositData?.tea, 2)}%`}
                                    />
                                </GridTable.Data> */}
                                <GridTable.Data columnStart="2" rowStart="3">
                                    <TransactionInfo
                                        field="deposits.tnaDetail"
                                        value={`${formatNum(depositData?.tna, 2)}%`}
                                    />
                                </GridTable.Data>
                                <GridTable.Data columnStart="3" rowStart="3">
                                    <TransactionInfo
                                        field="deposits.expirationAction.label"
                                        i18nId={`deposits.expirationAction.${depositData?.expirationAction}`}
                                    />
                                </GridTable.Data>
                                {depositData?.profit && (
                                    <>
                                        <GridTable.Data columnStart={1} columnWidth={3} rowStart={4} className="">
                                            <CustomData
                                                titleText="deposits.retentions.perceptions"
                                                titleTextType="I18N"
                                            />
                                        </GridTable.Data>

                                        <GridTable.Data columnStart={1} columnWidth={3} rowStart={5} className="">
                                            <TransactionInfo
                                                field="deposits.profits"
                                                value={`${currencyString} ${formatNum(depositData?.profit, 2)}`}
                                            />
                                        </GridTable.Data>
                                    </>
                                )}
                                {depositData?.stamp && (
                                    <>
                                        <GridTable.Data columnStart={1} columnWidth={3} rowStart={6} className="">
                                            <TransactionInfo
                                                field="deposits.stampCode"
                                                value={`${currencyString} ${formatNum(depositData?.stamp, 2)}`}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={3} rowStart={6} className="">
                                            <TransactionInfo
                                                field="deposits.jurisdiction"
                                                value={depositData?.jurisdiction}
                                            />
                                        </GridTable.Data>
                                    </>
                                )}
                            </GridTable.Container>
                        </GridTable>
                    </div>
                    <Formik
                        ref={(el) => setFormRef(el)}
                        validateOnBlur
                        validateOnChange={false}
                        initialValues={{
                            credential: "",
                        }}
                        validationSchema={Yup.object().shape({
                            credential: Yup.string().required(i18n.get("deposits.request.required")),
                        })}
                        onSubmit={handleSubmit}>
                        {({ isSubmitting }) => (
                            <Form className={isIOS && "mt-5"}>
                                <Row className="form-actions">
                                    <Col xs={12} md={4} mdOffset={2}>
                                        <Field
                                            idForm="form.credential"
                                            name="credential"
                                            labelText={`form.credential.${credentialType}.label`}
                                            placeholderText={credentialType === "otp" ? "Ej: 120573" : "Ej: Ju@n_123"}
                                            component={Credential}
                                            type={credentialType}
                                        />
                                    </Col>
                                    <Col xs={12} md={4} mdOffset={6} className="align-items-end">
                                        <div className={`form-group ${isDesktop && "mt-8"}`}>
                                            <div className="form-group">
                                                <div className="button-group">
                                                    <Button
                                                        block
                                                        type="submit"
                                                        label="global.send"
                                                        bsStyle="primary"
                                                        loading={isSubmitting && isValidating}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        )}
                    </Formik>
                </div>
            </MainContainer>
            {/* {!isDesktop && (
                <Options>
                    <AccountAction
                        isDesktop={isDesktop}
                        labelKey="accounts.agreements.new"
                        to={`/accounts/${account.idProduct}/agreements/request`}
                    />
                </Options>
            )} */}
        </Fragment>
    );
};

NewDepositSummaryForm.propTypes = {
    newDepositData: shape({}).isRequired,
    depositToken: string,
    depositTicketId: string,
    depositTicketStatus: string,
    depositRequiredCredentials: arrayOf(string).isRequired,
    dispatch: func.isRequired,
    fetching: bool,
};

NewDepositSummaryForm.defaultProps = {
    depositToken: "",
    depositTicketId: "",
    depositTicketStatus: "",
    fetching: false,
};

const mapStateToProps = (state) => ({
    newDepositData: depositsSelectors.getNewDepositData(state),
    depositToken: depositsSelectors.getDepositToken(state),
    depositRequiredCredentials: depositsSelectors.getDepositRequiredCredentials(state),
    depositTicketStatus: depositsSelectors.getDepositTicketStatus(state),
    depositTicketId: depositsSelectors.getDepositTicketId(state),
    fetching: depositsSelectors.getFetching(state),
});

export default connect(mapStateToProps)(NewDepositSummaryForm);
